import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { createProduct, getCompanies } from '../services/api';
import { Company } from '../types';
import './Modal.css';

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    hsn: '',
    price: '',
    sizes: '',
    colors: '',
    gstRate: '',
    companyId: '',
    storeName: ''
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData);
      // Don't auto-select company - let user choose
    } catch (err) {
      // Silent error handling
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.companyId) {
        setError('Please select a company');
        setLoading(false);
        return;
      }

      // Validate SKU: alphanumeric and spaces allowed
      const skuPattern = /^[A-Za-z0-9\s]+$/;
      if (!skuPattern.test(formData.sku)) {
        setError('SKU must contain only letters, numbers, and spaces');
        setLoading(false);
        return;
      }

      const productData = {
        sku: formData.sku,
        name: formData.name,
        description: formData.description,
        hsn: formData.hsn,
        price: parseFloat(formData.price),
        sizes: formData.sizes.split(',').map(s => s.trim()).filter(s => s),
        colors: formData.colors.split(',').map(c => c.trim()).filter(c => c),
        gstRate: formData.gstRate ? parseFloat(formData.gstRate) : 0,
        companyId: formData.companyId,
        storeName: formData.storeName
      };

      await createProduct(productData);
      
      // Reset form
      setFormData({
        sku: '',
        name: '',
        description: '',
        hsn: '',
        price: '',
        sizes: '',
        colors: '',
        gstRate: '',
        companyId: '',
        storeName: ''
      });
      
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Product</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="companyId">Company *</label>
              <select
                id="companyId"
                name="companyId"
                value={formData.companyId}
                onChange={handleChange}
                required
              >
                <option value="">Select a company</option>
                {companies.map((company) => (
                  <option key={company._id || company.id} value={company._id || company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="storeName">Store Name</label>
              <input
                type="text"
                id="storeName"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                placeholder="e.g., Main Store, Branch A, Store 1"
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Enter store name to organize products by location
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sku">SKU *</label>
              <input
                type="text"
                id="sku"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
                placeholder="e.g., PLRSET 6, ABC 123"
                pattern="[A-Za-z0-9\s]+"
                title="Letters, numbers, and spaces allowed"
              />
              <small style={{ color: '#6b7280', fontSize: '12px' }}>
                Letters, numbers, and spaces allowed (e.g., PLRSET 6, ABC 123, Product 001)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="hsn">HSN Code *</label>
              <input
                type="text"
                id="hsn"
                name="hsn"
                value={formData.hsn}
                onChange={handleChange}
                required
                placeholder="e.g., 820559"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="name">Product Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Screwdriver Kit"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={3}
              placeholder="e.g., 100 in 1 Precision Screwdriver Set with Magnetic Driver Kit"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price (₹) *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="e.g., 299"
              />
            </div>

            <div className="form-group">
              <label htmlFor="gstRate">GST Rate (%)</label>
              <input
                type="number"
                id="gstRate"
                name="gstRate"
                value={formData.gstRate}
                onChange={handleChange}
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 12"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sizes">Sizes (comma-separated)</label>
              <input
                type="text"
                id="sizes"
                name="sizes"
                value={formData.sizes}
                onChange={handleChange}
                placeholder="e.g., Compact, Standard"
              />
            </div>

            <div className="form-group">
              <label htmlFor="colors">Colors (comma-separated)</label>
              <input
                type="text"
                id="colors"
                name="colors"
                value={formData.colors}
                onChange={handleChange}
                placeholder="e.g., Red, Blue, Green"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;
